import type { RouteLocationRaw } from 'vue-router';
import { EntitySearchType, type EntitySearchHitDto } from '@/ApiClient';
import { stringifyFilters } from '@lib/filter-query-utils';
import { blobUrl } from '@lib/api';
import { useHierarchy } from '@/store/hierarchy';

import { useMChartsLabel } from '@lib/mchart-label';

import { componentsIconScale, navIcons } from '@lib/nav-icons';

export interface EntitySearchKind {
  type: EntitySearchType;
  /** Label of the entry that opens this search. */
  label: string;
  /** Shorter name for the breadcrumb chip once inside the search. */
  breadcrumb: string;
  placeholder: string;
  /** Plural noun for the "type to search" hint and the empty state. */
  noun: string;
  icon: string;
  /** Artwork drawn inside its own padding, scaled up so it fills the row's icon box like the rest. */
  iconScale?: number;
  keywords: string;
  /** Chord that opens this search straight from anywhere, e.g. 's p'. */
  shortcut: string;
  route: (hit: EntitySearchHitDto) => RouteLocationRaw;
  /**
   * Per-hit thumbnail, falling back to an inline icon when the entity has no image.
   * Products fall back to their hierarchy node's icon, the way the product picker does.
   */
  rowIcon?: (hit: EntitySearchHitDto) => string;
}

function thumbnail(hit: EntitySearchHitDto): string | undefined {
  return hit.image ? `${blobUrl}/${hit.image}` : undefined;
}

export const entitySearchKinds: EntitySearchKind[] = [
  {
    type: EntitySearchType.Product,
    label: 'Search for products',
    breadcrumb: 'Products',
    placeholder: 'Name, product number or article number…',
    noun: 'products',
    icon: navIcons.products,
    keywords: 'find product article style number',
    shortcut: 's p',
    route: (hit) => ({ name: 'product', params: { id: hit.id } }),
    rowIcon: (hit) => useHierarchy().getIconWithFallback(hit.hierarchyNodeId ?? undefined),
  },
  {
    type: EntitySearchType.Order,
    label: 'Search for orders',
    breadcrumb: 'Orders',
    placeholder: 'Order number, external number or supplier…',
    noun: 'orders',
    icon: navIcons.orders,
    keywords: 'find order purchase po number supplier',
    shortcut: 's o',
    route: (hit) => ({ name: 'orders', params: { orderNumber: hit.reference } }),
  },
  {
    type: EntitySearchType.Component,
    label: 'Search for components',
    breadcrumb: 'Components',
    placeholder: 'Name, component number or reference…',
    noun: 'components',
    icon: navIcons.components,
    iconScale: componentsIconScale,
    keywords: 'find component material fabric trim number',
    shortcut: 's c',
    // Components have no route of their own, so a hit lands on the overview
    // filtered down to that one component — the same state as typing its
    // number into the page's own search.
    route: (hit) => ({ name: 'components', query: { filters: stringifyFilters({ searchTerm: hit.reference }) } }),
  },
  {
    type: EntitySearchType.Sample,
    label: 'Search for samples',
    breadcrumb: 'Samples',
    placeholder: 'Round number, product or supplier…',
    noun: 'sample rounds',
    icon: navIcons.samples,
    keywords: 'find sample round fitting proto',
    shortcut: 's s',
    route: (hit) => ({ name: 'samples', params: { roundNumber: hit.reference } }),
    rowIcon: (hit) => useHierarchy().getIconWithFallback(hit.hierarchyNodeId ?? undefined),
  },
  {
    // The measurement chart's name is brand-configurable, so these labels follow it.
    type: EntitySearchType.MeasurementChart,
    get label() {
      return `Search for ${useMChartsLabel().value.toLowerCase()}`;
    },
    get breadcrumb() {
      return useMChartsLabel().value;
    },
    get noun() {
      return useMChartsLabel().value.toLowerCase();
    },
    placeholder: 'Name or reference number…',
    icon: navIcons.measurements,
    keywords: 'find measurement chart list size spec grading',
    shortcut: 's m',
    // The full detail page, not the overview's fly-in.
    route: (hit) => ({ name: 'measurement-chart', params: { id: hit.id } }),
  },
  {
    type: EntitySearchType.Supplier,
    label: 'Search for suppliers',
    breadcrumb: 'Suppliers',
    placeholder: 'Supplier name…',
    noun: 'suppliers',
    icon: navIcons.suppliers,
    keywords: 'find supplier vendor factory manufacturer',
    shortcut: 's u',
    // Suppliers have no route of their own either — the overview filtered to the name.
    route: (hit) => ({ name: 'suppliers', query: { filters: stringifyFilters({ searchTerm: hit.title }) } }),
  },
];

export function entitySearchKind(type: EntitySearchType): EntitySearchKind {
  return entitySearchKinds.find((kind) => kind.type === type)!;
}

/** The image and icon one hit's row draws: the entity's own photo, else a fallback icon. */
export function entitySearchHitIcon(kind: EntitySearchKind, hit: EntitySearchHitDto) {
  const rowIcon = kind.rowIcon?.(hit);
  // A hierarchy icon is the node's own artwork, so the kind's padding correction doesn't apply.
  return { image: thumbnail(hit), icon: rowIcon ?? kind.icon, iconScale: rowIcon ? undefined : kind.iconScale };
}
